// Duplicates Detection Service
// Connects to backend duplicate detection API

const API_BASE = process.env.NODE_ENV === 'production' 
  ? 'https://api.explanatum.co.uk'
  : 'http://localhost:3000';

export interface DuplicateMatch {
  id: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  similarityScore: number;
  matchedFields: string[];
  created_at: string;
}

export interface MergeResult {
  merged_id: string;
  source_ids: string[];
  merged_data: any;
}

const duplicatesService = {
  /**
   * Detect duplicates for an entity
   */
  async detectDuplicates(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string,
    threshold: number = 0.7
  ): Promise<DuplicateMatch[]> {
    const response = await fetch(
      `${API_BASE}/api/crm/duplicates/detect?organization_id=${organizationId}&entity_type=${entityType}&entity_id=${entityId}&threshold=${threshold}`,
      {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to detect duplicates');
    }

    const { data } = await response.json();
    return data || [];
  },

  /**
   * Merge duplicate records
   */
  async mergeDuplicates(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    primaryId: string,
    duplicateIds: string[],
    mergeStrategy?: any
  ): Promise<MergeResult> {
    const response = await fetch(`${API_BASE}/api/crm/duplicates/merge`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        organization_id: organizationId,
        entity_type: entityType,
        primary_id: primaryId,
        duplicate_ids: duplicateIds,
        merge_strategy: mergeStrategy
      })
    });

    if (!response.ok) {
      throw new Error('Failed to merge duplicates');
    }

    const { data } = await response.json();
    return data;
  },

  /**
   * Get merge history
   */
  async getMergeHistory(
    organizationId: string,
    entityType?: string,
    limit: number = 50
  ): Promise<any[]> {
    let url = `${API_BASE}/api/crm/duplicates/history?organization_id=${organizationId}&limit=${limit}`;
    if (entityType) {
      url += `&entity_type=${entityType}`;
    }

    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch merge history');
    }

    const { data } = await response.json();
    return data || [];
  },

  /**
   * Alias for detectDuplicates to match naming convention
   */
  async findDuplicates(
    organizationId: string,
    entityType: 'contact' | 'company' | 'deal',
    entityId: string,
    threshold: number = 0.7
  ): Promise<{ data: DuplicateMatch[] }> {
    const data = await this.detectDuplicates(organizationId, entityType, entityId, threshold);
    return { data };
  }
};

export default duplicatesService;
